import type {
  IsolationRequiredFlagDetails,
  PatientFlag,
} from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { format, parseISO } from 'date-fns'
import { orderBy } from 'lodash-es'
import { escapeHtml } from '#src/components/rich-text-editor/conditionNoteHtml.ts'

export type IsolationRequiredFlagOverlay = 'add' | 'remove' | null

export function formatIsolationRequiredSetOn(value: string): string {
  const date = parseISO(value)
  return `${format(date, 'M/d/yy, h:mm')}${format(date, 'a').toUpperCase()}`
}

export function latestIsolationRequiredNotes(
  versions: readonly PatientFlag[],
  fallbackFlags: readonly PatientFlag[] = [],
  flagId?: string
): string {
  const candidates = [...versions, ...fallbackFlags].filter((flag) => {
    if (!flagId) {
      return false
    }
    return flag.toFhirResource().id === flagId
  })
  const timed = orderBy(
    candidates,
    [(flag) => flag.toFhirResource().meta?.lastUpdated ?? ''],
    ['desc']
  )
  return timed.find((flag) => flag.oosaNotes)?.oosaNotes ?? ''
}

export function mergeIsolationRequiredDetails(
  persisted?: IsolationRequiredFlagDetails,
  fallback?: IsolationRequiredFlagDetails
): IsolationRequiredFlagDetails | undefined {
  const reason = fallback?.reason ?? persisted?.reason
  const setOn = fallback?.setOn ?? persisted?.setOn
  if (!reason || !setOn) {
    return undefined
  }
  return {
    setOn,
    reason,
    notes: fallback != null ? fallback.notes : (persisted?.notes ?? ''),
  }
}

export function applyIsolationRequiredFlagOverlay(
  flagCodes: readonly patients.ParticipantFlagCode[],
  overlay: IsolationRequiredFlagOverlay
): patients.ParticipantFlagCode[] {
  if (overlay === 'add') {
    return flagCodes.includes(patients.ParticipantFlagCode.ISOLATION_REQUIRED)
      ? [...flagCodes]
      : [...flagCodes, patients.ParticipantFlagCode.ISOLATION_REQUIRED]
  }
  if (overlay === 'remove') {
    return flagCodes.filter(
      (code) => code !== patients.ParticipantFlagCode.ISOLATION_REQUIRED
    )
  }
  return [...flagCodes]
}

export type IsolationRequiredTimelineEventAction = 'set on' | 'modified' | 'off'

export function isolationRequiredTimelineEventMessage(
  details: IsolationRequiredFlagDetails | undefined,
  action: IsolationRequiredTimelineEventAction
): string {
  // One <p> so TipTap does not leave a last-paragraph gap at the bottom
  // of the timeline card.
  const lines = [`<strong>Isolation Required ${action}</strong>`]
  if (action !== 'off' && details) {
    lines.push(
      `Reason: ${escapeHtml(
        patients.formatIsolationRequiredReasonDisplay(details.reason)
      )}`
    )
    const notes = details.notes.trim()
    if (notes) {
      lines.push(`Notes: ${escapeHtml(notes).replace(/\r\n|\r|\n/g, '<br>')}`)
    }
  }
  return `<p>${lines.join('<br>')}</p>`
}
